import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { companyInfo } from '../mock';
import { Award, Heart, Shield, Users } from 'lucide-react';

const About = () => {
  return (
    <div className="bg-white min-h-screen">
      {/* Hero */}
      <section className="bg-gradient-to-b from-slate-50 to-white py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl font-bold text-slate-900 mb-4">About Capitol View Stay</h1>
          <p className="text-xl text-slate-600">{companyInfo.tagline}</p>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <Card>
            <CardContent className="p-8">
              <h2 className="text-2xl font-bold text-slate-900 mb-4">Our Story</h2>
              <p className="text-slate-600 leading-relaxed mb-4">
                {companyInfo.about}
              </p>
              <p className="text-slate-600 leading-relaxed">
                Since {companyInfo.yearEstablished}, we have hosted thousands of guests from around the world, 
                helping them experience the best of Washington DC. Whether you're visiting for business, leisure, 
                or an extended stay, we're committed to making your time in the nation's capital comfortable, 
                convenient, and memorable.
              </p>
            </CardContent>
          </Card>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-16 bg-slate-50">
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="text-3xl font-bold text-slate-900 mb-4">Our Values</h2>
            <p className="text-lg text-slate-600">What drives us every day</p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-8 pb-8">
                <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Heart className="h-8 w-8 text-slate-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Guest First</h3>
                <p className="text-sm text-slate-600">
                  Your comfort and satisfaction are our top priorities. We go above and beyond to ensure every stay exceeds expectations.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-8 pb-8">
                <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Award className="h-8 w-8 text-slate-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Excellence</h3>
                <p className="text-sm text-slate-600">
                  We maintain the highest standards in property selection, cleanliness, and amenities to deliver premium experiences.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-8 pb-8">
                <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Shield className="h-8 w-8 text-slate-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Trust</h3>
                <p className="text-sm text-slate-600">
                  Transparent pricing, honest communication, and reliable service you can count on for every reservation.
                </p>
              </CardContent>
            </Card>

            <Card className="text-center hover:shadow-lg transition-shadow duration-300">
              <CardContent className="pt-8 pb-8">
                <div className="h-16 w-16 rounded-full bg-slate-100 flex items-center justify-center mx-auto mb-4">
                  <Users className="h-8 w-8 text-slate-700" />
                </div>
                <h3 className="text-lg font-bold text-slate-900 mb-2">Community</h3>
                <p className="text-sm text-slate-600">
                  We're proud to be part of the DC community and help visitors discover what makes this city special.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-16">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Why Choose Capital View LLC?</h2>
          <div className="space-y-6">
            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Prime Locations</h3>
                <p className="text-slate-600">
                  All our properties are strategically located in Washington DC's most sought-after neighborhoods, 
                  providing easy access to metro stations, restaurants, attractions, and business districts.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Fully Furnished & Equipped</h3>
                <p className="text-slate-600">
                  Each property comes fully furnished with modern amenities, high-speed WiFi, smart TVs, 
                  well-equipped kitchens, and comfortable workspaces—everything you need for a comfortable stay.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Professional Management</h3>
                <p className="text-slate-600">
                  Our experienced team handles everything from booking to checkout, ensuring smooth operations 
                  and prompt responses to any questions or concerns during your stay.
                </p>
              </CardContent>
            </Card>

            <Card>
              <CardContent className="p-6">
                <h3 className="text-xl font-bold text-slate-900 mb-2">Flexible & Transparent</h3>
                <p className="text-slate-600">
                  We offer flexible booking policies, transparent pricing with no hidden fees, and options 
                  for short-term stays, extended visits, and everything in between.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-slate-800 text-white">
        <div className="mx-auto max-w-4xl px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl font-bold mb-4">Experience the Capitol View Stay Difference</h2>
          <p className="text-lg text-slate-300 mb-8">
            Join thousands of satisfied guests who have made Capitol View Stay their home away from home in Washington DC.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="/properties" className="inline-block">
              <button className="bg-white text-slate-800 hover:bg-slate-100 px-8 py-3 rounded-md font-medium transition-colors duration-200">
                View Our Properties
              </button>
            </a>
            <a href="/contact" className="inline-block">
              <button className="bg-transparent border-2 border-white text-white hover:bg-white hover:text-slate-800 px-8 py-3 rounded-md font-medium transition-colors duration-200">
                Contact Us
              </button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default About;